import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  ReferenceLine,
} from 'recharts';
import React, { useState } from 'react';
import { networksMetrics } from './components/networksMetrics';
import ChannelsTooltip from './components/ChannelsTooltip';
import Ninja from '@ninja';
import NoResultFound from 'components/charts/components/NoResultFound';
import { Tooltip as AntdTooltip, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import getShortFormattedNumber from 'store/redux/utils/getShortFormattedNumber';

const ChannelChart = ({
  data,
  height,
  selected_metrics,
  isLoading,
  showYAxis = false,
  showXAxis = false,
}) => {
  const [activeIndex, setActiveIndex] = useState(null);
  const [hiddenNetworks, setHiddenNetworks] = useState([]);

  const toggleNetwork = (networkKey) => {
    setHiddenNetworks((prev) =>
      prev.includes(networkKey) ? prev.filter((n) => n !== networkKey) : [...prev, networkKey]
    );
  };

  const getBarStyle = (index) => {
    if (activeIndex === null) {
      return { opacity: 1, transition: 'all 0.3s ease-in-out' };
    }
    return {
      opacity: index === activeIndex ? 1 : 0.2,
      transition: 'all 0.3s ease-in-out',
    };
  };

  const handleMouseMove = (e) => {
    if (e?.activeTooltipIndex !== undefined) {
      setActiveIndex(e.activeTooltipIndex);
    }
  };

  if (isLoading) {
    return (
      <Spin
        size='large'
        indicator={<LoadingOutlined spin className='text-gray' />}
        style={{ height: `${height}px` }}
        className='flex items-center justify-center w-full h-full'
      />
    );
  }

  if (!data) {
    return <NoResultFound />;
  }

  return (
    <ResponsiveContainer width='100%' height={height}>
      <BarChart
        onMouseLeave={() => setActiveIndex(null)}
        maxBarSize={40}
        onMouseMove={handleMouseMove}
        data={data}
        stackOffset='sign'
        margin={{
          left: !showXAxis ? -25 : 0,
        }}
      >
        <CartesianGrid strokeDasharray='2 2' stroke={Ninja.colors.xlight} vertical={false} />
        <XAxis
          hide={showXAxis}
          dataKey='date'
          axisLine={false}
          dy={3}
          tick={{ fontSize: 12, fontWeight: '400', fill: Ninja.colors.dark }}
          tickFormatter={(value) =>
            new Date(value).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: '2-digit',
            })
          }
        />
        <YAxis
          hide={showYAxis}
          axisLine={false}
          tickFormatter={(value) => getShortFormattedNumber(value)}
          tick={{ fontSize: 12, fontWeight: '400', fill: Ninja.colors.dark }}
        />

        <Tooltip
          cursor={{
            fill: Ninja.colors.info,
            fillOpacity: 0.2,
            transition: 'all 0.2s ease-in-out',
          }}
          content={({ payload, label }) => {
            return (
              <ChannelsTooltip
                payload={payload}
                label={label}
                selected_metrics={selected_metrics}
              />
            );
          }}
        />
        <ReferenceLine y={0} stroke={Ninja.colors.xlight} />

        <Legend
          verticalAlign='bottom'
          height={30}
          content={({ payload }) => (
            <div
              style={{
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              {payload.map((entry) => {
                const isHidden = hiddenNetworks.includes(entry.id);

                return (
                  <div
                    key={entry.id}
                    className='flex items-center gap-1 cursor-pointer'
                    onClick={() => toggleNetwork(entry.id)}
                    style={{ opacity: isHidden ? 0.3 : 1, transition: 'opacity 0.3s' }}
                  >
                    <div
                      style={{
                        width: 16,
                        height: 16,
                        backgroundColor: entry.color,
                        borderRadius: 3,
                      }}
                    ></div>
                    <AntdTooltip title={entry.payload.label}>
                      <img
                        src={entry.payload.icon}
                        alt={entry.payload.label}
                        width={16}
                        height={16}
                      />
                    </AntdTooltip>
                  </div>
                );
              })}
            </div>
          )}
          payload={Object.values(networksMetrics).map((network) => ({
            id: network.key,
            color: network.color,
            payload: network,
          }))}
        />

        {Object.keys(networksMetrics).map((networkKey, index) => {
          const network = networksMetrics[networkKey];
          const isHidden = hiddenNetworks.includes(networkKey);

          if (isHidden) return null;

          return (
            <Bar
              key={index}
              dataKey={`${selected_metrics}.${networkKey}`}
              stackId='a'
              shape={({ x, y, width, height, index }) => {
                const radius = 3;
                const gap = 2;
                const isNegative = height < 0;
                return (
                  <rect
                    x={x}
                    y={isNegative ? y + height + gap / 2 : y + gap / 2}
                    width={width}
                    height={Math.max(0, Math.abs(height) - gap)}
                    rx={radius}
                    ry={radius}
                    fill={network.color}
                    style={getBarStyle(index)}
                  />
                );
              }}
            />
          );
        })}
      </BarChart>
    </ResponsiveContainer>
  );
};

export default ChannelChart;
